import { toast } from "@win-reality/react-ui";
import { useCallback, useEffect, useRef, useState } from "react";

import { handleErrorNotification } from "@/pages/Tagging/utils/handleErrorNotification";

import { toActivity } from "../api/gameSnapshot";
import { GameStreamEvent } from "../api/gameStream";
import { useGameWrites } from "../api/useGameWrites";
import {
  applyTagOverrides,
  getStateBeforePitch,
  PitchTags,
} from "../utils/pitchTagging";
import { doesTagUpdateGameState } from "../utils/plateAppearanceFeed";
import { useScorekeepingGame } from "./useScorekeepingGame";

/**
 * How long a tag save can stay pending before Save is let go anyway, so a
 * missed WebSocket message or a hung request (omnibus requests have no
 * timeout) can't lock tagging.
 */
const TAG_ARRIVAL_TIMEOUT_MS = 30_000;

/** A pitch's tag save that hasn't finished. */
type PendingTag = {
  /** Which save this is, so an older save finishing can't clear a newer one */
  save: number;
  pitchResult: string | null;
  /** Done when the game service sends the pitch back tagged, not when the request ends */
  awaitsStream: boolean;
  /**
   * The game service only sends a resulting game state for a tag that moves
   * it (spec 3.4); a late tag on an earlier pitch comes back without one
   */
  updatesGameState: boolean;
  timeout: ReturnType<typeof setTimeout>;
};

/**
 * Save Tags: shows the tags and moves on to the next pitch right away, then
 * tags the pitch through omnibus in the background (the pitch's row shows a
 * spinner meanwhile). A failed save puts the pitch's tags back. The game
 * service ends and starts at bats from the tag, over its WebSocket.
 *
 * The game service counts tags in the order they reach it, not the order the
 * pitches were thrown. So Save stays disabled until the previous tag comes
 * back over the WebSocket: the user can pick the next pitch's tags meanwhile,
 * but tags can't overtake each other. The request finishing isn't enough,
 * because omnibus only queues the tag.
 */
export const useTagSaves = ({
  game,
  writes,
}: {
  game: ReturnType<typeof useScorekeepingGame>;
  writes: ReturnType<typeof useGameWrites>;
}) => {
  const pending = useRef(new Map<string, PendingTag>());
  // Mirrors `pending` for rendering
  const [pendingTagIds, setPendingTagIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const tagSaveCount = useRef(0);

  useEffect(() => {
    const tags = pending.current;
    return () => tags.forEach((tag) => clearTimeout(tag.timeout));
  }, []);

  const isLatest = (activityId: string, save: number) =>
    pending.current.get(activityId)?.save === save;

  const finish = useCallback((activityId: string) => {
    clearTimeout(pending.current.get(activityId)?.timeout);
    pending.current.delete(activityId);
    setPendingTagIds(new Set(pending.current.keys()));
  }, []);

  /**
   * Finishes a pending tag once the game service sends its pitch back tagged
   * with it, along with the game state it led to.
   */
  const receiveStreamEvent = useCallback(
    (event: GameStreamEvent) => {
      if (!("activity" in event)) return;
      const activity = toActivity(event.activity);
      if (!activity?.result) return;
      const tag = pending.current.get(activity.activityId);
      if (!tag?.awaitsStream) return;
      if (tag.updatesGameState && !activity.resultingState) return;
      if (tag.pitchResult !== activity.result.pitchResult) return;
      finish(activity.activityId);
    },
    [finish],
  );

  const saveTags = async (activityId: string, tags: PitchTags) => {
    const previous = game.data.activities.find(
      (a) => a.activityId === activityId,
    );
    const updatesGameState = doesTagUpdateGameState(game.data, activityId);
    // Read before tagging: once tagged, the pitch's state is carried over from
    // the pitch before, which misses Edit Live Game State edits
    const stateBefore = previous
      ? getStateBeforePitch(game.data, previous)
      : null;
    const tagged = applyTagOverrides(game.data, { [activityId]: tags });
    game.saveTags(activityId, tags);
    if (!writes.canWrite) return true;

    const save = ++tagSaveCount.current;
    // Not a Pitch moves no state, so its order doesn't matter
    const awaitsStream = tags.pitchResult !== "NotAPitch";
    clearTimeout(pending.current.get(activityId)?.timeout);
    pending.current.set(activityId, {
      save,
      pitchResult: tags.pitchResult,
      awaitsStream,
      updatesGameState,
      timeout: setTimeout(() => {
        if (!isLatest(activityId, save)) return;
        console.warn(
          `[scorekeeping] tag for ${activityId} still pending after ${TAG_ARRIVAL_TIMEOUT_MS / 1000}s`,
        );
        toast(
          awaitsStream
            ? "A saved tag hasn't reached the game yet. Check the count before saving the next pitch."
            : "A saved tag is taking a while. Check it saved before relying on it.",
        );
        finish(activityId);
      }, TAG_ARRIVAL_TIMEOUT_MS),
    });
    setPendingTagIds(new Set(pending.current.keys()));

    try {
      await writes.tagPitch(tagged, activityId, stateBefore);
      if (!awaitsStream && isLatest(activityId, save)) finish(activityId);
      return true;
    } catch (error) {
      handleErrorNotification("Couldn't save tags", error as Error);
      if (isLatest(activityId, save)) {
        if (previous) game.revertTags(previous);
        finish(activityId);
      }
      return false;
    }
  };

  return {
    saveTags,
    /** Pitches whose tags are still saving */
    pendingTagIds,
    /** A saved tag hasn't reached the game service yet, so Save must wait */
    isWaitingOnTag: pendingTagIds.size > 0,
    /** Call with every game service WebSocket event */
    receiveStreamEvent,
  };
};
